import { useCallback, useEffect, useRef, useState } from "react";
import type { FormikProps } from "formik";
import { isEmptyObject } from "../global/function";
import type { Preference } from "../queries/company.queries";
import type { BilledItem } from "../components/invoice-template/invoice-template.component";

// Shared VAT/ESF either-or calculation engine. Previously duplicated between
// invoice-form.component.jsx and customer-bulk-draft.component.jsx (the
// dedup deferred from the customer-bulk-draft migration — see
// migration_query_typescript memory). VAT and ESF are enforced as either/or
// in company preferences: whichever is active, an item's `vatable` flag
// decides which bucket (chargeable vs. non-taxable) it lands in, then
// discount/service-charge/VAT-or-ESF apply to the chargeable bucket only.
export interface BillTotalsValues {
	billed_items: BilledItem[];
	taxable_total_sales: number;
	non_taxable_total_sales: number;
	taxable_discount: number | string;
	non_taxable_discount: number | string;
	taxable_service_charge: number | string;
	non_taxable_service_charge: number | string;
	non_taxable_amt: number;
	taxable_sales_vat: number;
	amount_for_esf: number;
	vat: number;
	esf: number;
	total_sales: number;
	sub_total: number;
}

interface UseBillTotalsOptions<T extends BillTotalsValues> {
	formik: FormikProps<T>;
	preferenceDetails?: Partial<Preference>;
	// Fires whenever the computed total_sales actually changes (beyond a
	// small float tolerance), carrying the full would-be-next values plus
	// the previous total_sales. Only invoice-form's "bulk" mode consumes
	// this today (its own unsaved-changes diff tracking) — omit for callers
	// that don't need it (e.g. customer-bulk-draft).
	onTotalsChanged?: (nextValues: T, previousTotalSales: number) => void;
}

function areEqualWithTolerance(a: number, b: number, tolerance = 0.1): boolean {
	return Math.abs(a - b) < tolerance;
}

export function useBillTotals<T extends BillTotalsValues>({
	formik,
	preferenceDetails,
	onTotalsChanged,
}: UseBillTotalsOptions<T>) {
	const [isServiceChargeEnabled, setIsServiceChargeEnabled] = useState(false);

	// calculateWithTax's deps below deliberately omit `formik` (see the
	// eslint-disable there) to dodge an infinite render loop, so it doesn't
	// get recreated when the caller switches to a different draft (e.g.
	// invoice-form's Duplicate button) before that draft's resetForm has
	// applied — leaving it holding the *previous* draft's formik.values
	// (invoice_number included) on every recompute after the switch. Route
	// its reads through this ref, kept current every render, instead of the
	// closed-over `formik` object so it can't go stale.
	const formikRef = useRef(formik);
	formikRef.current = formik;

	// total calculation with all dependancies
	// taxableTotalSales parameter is current taxable_total_sales value
	// nonTaxableTotalSales parameter is current non_taxable_total_sales value
	// taxableDiscount parameter is current taxableDiscount value
	// nonTaxableDiscount parameter is current nonTaxableDiscount value
	// includeService parameter is service charge toggler(true/false)
	// taxableServiceCharge parameter is taxable_service_charge value that had been overridden
	// nonTaxableServiceCharge parameter is non_taxable_service_charge value that had been overridden
	const calculateWithTax = useCallback(
		(
			taxableTotalSales: number,
			nonTaxableTotalSales: number,
			taxableDiscount: number,
			nonTaxableDiscount: number,
			includeService: boolean,
			taxableServiceCharge = 0,
			nonTaxableServiceCharge = 0
		) => {
			let newTaxableServiceCharge = 0,
				newNonTaxableServiceCharge = 0,
				newTaxableGrossTotal = 0,
				newNonTaxableGrossTotal = 0;

			if (taxableTotalSales - taxableDiscount < 0) {
				formikRef.current.setFieldValue("taxable_discount", 0);
				taxableDiscount = 0;
			}
			if (nonTaxableTotalSales - nonTaxableDiscount < 0) {
				formikRef.current.setFieldValue("non_taxable_discount", 0);
				nonTaxableDiscount = 0;
			}

			if (taxableServiceCharge <= 0 || nonTaxableServiceCharge <= 0) {
				if (includeService) {
					newTaxableServiceCharge =
						(taxableTotalSales - taxableDiscount) *
						((preferenceDetails?.service_charge ?? 0) / 100);
					newNonTaxableServiceCharge =
						(nonTaxableTotalSales - nonTaxableDiscount) *
						((preferenceDetails?.service_charge ?? 0) / 100);
				} else {
					newTaxableServiceCharge = 0;
					newNonTaxableServiceCharge = 0;
				}
			}
			if (taxableServiceCharge > 0) {
				newTaxableServiceCharge = parseFloat(String(taxableServiceCharge || 0));
			}
			if (nonTaxableServiceCharge > 0) {
				newNonTaxableServiceCharge = parseFloat(
					String(nonTaxableServiceCharge || 0)
				);
			}

			newTaxableGrossTotal =
				taxableTotalSales - taxableDiscount + newTaxableServiceCharge;
			newNonTaxableGrossTotal =
				nonTaxableTotalSales -
				nonTaxableDiscount +
				newNonTaxableServiceCharge;

			formikRef.current.setFieldValue(
				"taxable_service_charge",
				newTaxableServiceCharge.toFixed(2)
			);
			formikRef.current.setFieldValue(
				"non_taxable_service_charge",
				newNonTaxableServiceCharge.toFixed(2)
			);

			let eat,
				vat = 0,
				esfAmount = 0;

			// VAT and ESF are either/or (enforced in company preferences). Whichever
			// is active, newTaxableGrossTotal is the "chargeable" bucket (built from
			// items marked vatable, see computedSubTotal) and newNonTaxableGrossTotal
			// is the rest — the same split either way, just fed into a different pair
			// of output fields (taxable_sales_vat+vat vs amount_for_esf+esf).
			const vatModeActive = !!preferenceDetails?.vat;
			const esfModeActive = !vatModeActive && (preferenceDetails?.esf ?? 0) > 0;

			let finalTaxableSalesVat = 0;
			let finalAmountForEsf = 0;

			if (vatModeActive) {
				finalTaxableSalesVat = newTaxableGrossTotal;
				vat = newTaxableGrossTotal > 0 ? 0.13 * newTaxableGrossTotal : 0;
			} else if (esfModeActive) {
				finalAmountForEsf = newTaxableGrossTotal;
				esfAmount =
					newTaxableGrossTotal > 0
						? newTaxableGrossTotal * ((preferenceDetails?.esf ?? 0) / 100)
						: 0;
			}

			formikRef.current.setFieldValue("non_taxable_amt", newNonTaxableGrossTotal);
			formikRef.current.setFieldValue("taxable_sales_vat", finalTaxableSalesVat);
			formikRef.current.setFieldValue("amount_for_esf", finalAmountForEsf);
			formikRef.current.setFieldValue("esf", esfAmount);

			eat =
				newNonTaxableGrossTotal +
				finalTaxableSalesVat +
				finalAmountForEsf +
				vat +
				esfAmount;

			// Read via formikRef (not the closed-over `formik`), since this
			// closure only refreshes when the deps array below changes — a
			// stale `formik.values` here previously leaked the *previous*
			// draft's invoice_number into onTotalsChanged's payload whenever
			// the caller switched drafts (e.g. Duplicate) right before this
			// ran, misattributing the edit to the wrong draft.
			const previousTotalSales = formikRef.current.values.total_sales;

			formikRef.current.setFieldValue("vat", vat);
			formikRef.current.setFieldValue("total_sales", eat);

			if (onTotalsChanged && !areEqualWithTolerance(eat, previousTotalSales)) {
				onTotalsChanged(
					{
						...formikRef.current.values,
						taxable_service_charge: newTaxableServiceCharge,
						non_taxable_service_charge: newNonTaxableServiceCharge,
						taxable_sales_vat: finalTaxableSalesVat,
						non_taxable_amt: newNonTaxableGrossTotal,
						vat,
						amount_for_esf: finalAmountForEsf,
						esf: esfAmount,
						total_sales: eat,
					},
					previousTotalSales
				);
			}
		},
		// eslint-disable-next-line
		[
			preferenceDetails?.service_charge,
			preferenceDetails?.esf,
			preferenceDetails?.vat,
			onTotalsChanged,
		]
	);

	// Recomputes taxable/non-taxable subtotals from billed_items, then feeds
	// them straight into calculateWithTax with fresh values (not via a second
	// effect reacting to the totals it just wrote — invoice-form's original
	// direct-call approach, adopted here over customer-bulk-draft's indirect
	// two-hop version for the same result with one fewer render round-trip).
	const computedSubTotal = useCallback(() => {
		// VAT and ESF are either/or — whichever is active, an item's `vatable`
		// flag decides whether it's chargeable or not; with neither active,
		// everything is non-chargeable.
		const chargeModeActive =
			!!preferenceDetails?.vat || (preferenceDetails?.esf ?? 0) > 0;

		const { taxableSalesTotal, nonTaxableSalesTotal } =
			formik.values.billed_items.reduce(
				(acc, item) => {
					if (chargeModeActive) {
						if (item.vatable) {
							acc.taxableSalesTotal += parseFloat(
								String(item?.total_price || 0)
							);
						} else {
							acc.nonTaxableSalesTotal += parseFloat(
								String(item?.total_price || 0)
							);
						}
					} else {
						// Neither VAT nor ESF enabled: nothing is chargeable, so route
						// everything through the non-taxable bucket instead of
						// overloading the "taxable" fields
						acc.nonTaxableSalesTotal += parseFloat(
							String(item?.total_price || 0)
						);
					}
					return acc;
				},
				{ taxableSalesTotal: 0, nonTaxableSalesTotal: 0 }
			);

		const totalSales = taxableSalesTotal + nonTaxableSalesTotal;

		formik.setFieldValue("non_taxable_total_sales", nonTaxableSalesTotal);
		formik.setFieldValue("taxable_total_sales", taxableSalesTotal);
		formik.setFieldValue("sub_total", totalSales);

		calculateWithTax(
			taxableSalesTotal,
			nonTaxableSalesTotal,
			Number(formik.values.taxable_discount),
			Number(formik.values.non_taxable_discount),
			isServiceChargeEnabled,
			parseFloat(String(formik.values.taxable_service_charge || 0)),
			parseFloat(String(formik.values.non_taxable_service_charge || 0))
		);
		// eslint-disable-next-line
	}, [
		formik.values.billed_items,
		preferenceDetails?.vat,
		preferenceDetails?.esf,
		isServiceChargeEnabled,
		calculateWithTax,
	]);

	// Recompute whenever billed_items changes (add/remove/edit a line). Skips
	// until preferenceDetails has actually loaded, so a first render can't
	// compute against an undefined VAT/ESF mode.
	useEffect(() => {
		if (
			!isEmptyObject(preferenceDetails) &&
			Array.isArray(formik.values.billed_items) &&
			formik.values.billed_items.length > 0
		) {
			computedSubTotal();
		}
		// eslint-disable-next-line
	}, [formik.values.billed_items, computedSubTotal, preferenceDetails]);

	// track changes to billed_items, it is an array so last update was not pushed so manually pushed item value
	const handleBilledItemsChange = (
		fieldName: string,
		index: number,
		itemName: string,
		newValue: string | number | boolean
	) => {
		const billedItems = structuredClone(
			formik.values.billed_items
		) as BilledItem[];

		(billedItems[index] as Record<string, unknown>)[itemName] = newValue;

		billedItems[index].total_price =
			Number(billedItems[index].item_price) *
			Number(billedItems[index].item_qty);

		formik.setFieldValue("billed_items", billedItems);
	};

	// track changes in discount and service charge, also trigger total computation
	const handleDiscountServiceChange = (
		fieldName: string,
		fieldValue: number
	) => {
		let taxableDiscount = 0,
			nonTaxableDiscount = 0,
			taxableServiceCharge = 0,
			nonTaxableServiceCharge = 0;

		switch (fieldName) {
			case "non_taxable_discount":
				nonTaxableDiscount = fieldValue;
				if (nonTaxableDiscount > formik.values.non_taxable_total_sales) {
					formik.setFieldError(
						"non_taxable_discount",
						"Amount Exceeds sales amount"
					);
					return;
				}
				calculateWithTax(
					formik.values.taxable_total_sales,
					formik.values.non_taxable_total_sales,
					Number(formik.values.taxable_discount),
					nonTaxableDiscount,
					isServiceChargeEnabled,
					parseFloat(String(formik.values.taxable_service_charge)),
					0
				);
				break;
			case "taxable_discount":
				taxableDiscount = fieldValue;
				if (taxableDiscount > formik.values.taxable_total_sales) {
					formik.setFieldError(
						"taxable_discount",
						"Amount Exceeds sales amount"
					);
					return;
				}
				calculateWithTax(
					formik.values.taxable_total_sales,
					formik.values.non_taxable_total_sales,
					taxableDiscount,
					Number(formik.values.non_taxable_discount),
					isServiceChargeEnabled,
					0,
					parseFloat(String(formik.values.non_taxable_service_charge))
				);
				break;
			case "non_taxable_service_charge":
				nonTaxableServiceCharge = fieldValue;
				calculateWithTax(
					formik.values.taxable_total_sales,
					formik.values.non_taxable_total_sales,
					Number(formik.values.taxable_discount),
					Number(formik.values.non_taxable_discount),
					isServiceChargeEnabled,
					parseFloat(String(formik.values.taxable_service_charge)),
					parseFloat(String(nonTaxableServiceCharge))
				);
				break;
			case "taxable_service_charge":
				taxableServiceCharge = fieldValue;
				calculateWithTax(
					formik.values.taxable_total_sales,
					formik.values.non_taxable_total_sales,
					Number(formik.values.taxable_discount),
					Number(formik.values.non_taxable_discount),
					isServiceChargeEnabled,
					parseFloat(String(taxableServiceCharge)),
					parseFloat(String(formik.values.non_taxable_service_charge))
				);
				break;
			default:
				break;
		}

		formik.setFieldValue(fieldName, fieldValue);
	};

	const handleServiceChargeToggle = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		setIsServiceChargeEnabled(e.target.checked);
		const taxableServiceCharge = e.target.checked
			? formik.values.taxable_service_charge
			: 0;
		const nonTaxableServiceCharge = e.target.checked
			? formik.values.non_taxable_service_charge
			: 0;

		calculateWithTax(
			formik.values.taxable_total_sales,
			formik.values.non_taxable_total_sales,
			Number(formik.values.taxable_discount),
			Number(formik.values.non_taxable_discount),
			e.target.checked,
			parseFloat(String(taxableServiceCharge)),
			parseFloat(String(nonTaxableServiceCharge))
		);
	};

	return {
		isServiceChargeEnabled,
		// Raw setter, alongside the toggle-event handler above — invoice-form
		// needs this to seed the flag when a draft with an existing service
		// charge loads (not a user toggle action).
		setIsServiceChargeEnabled,
		handleDiscountServiceChange,
		handleBilledItemsChange,
		handleServiceChargeToggle,
	};
}
